import React, { useState, useCallback } from 'react';
import { useTokenLifecycle } from '../hooks/useTokenLifecycle';
import type { KeycloakConfig } from '../config/types';

export interface TokenLifecycleManagerProps {
  /**
   * Keycloak configuration
   */
  config: KeycloakConfig;

  /**
   * Child components to render
   */
  children: React.ReactNode;

  /**
   * Enable debug logging
   * @default false
   */
  debug?: boolean;
}

/**
 * Component that manages token lifecycle including startup refresh and max lifetime tracking
 *
 * This component wraps your app and automatically:
 * - Checks tokens on app startup and refreshes if needed
 * - Tracks refresh token max lifetime
 * - Shows optional loading indicator during refresh
 *
 * @example
 * ```tsx
 * <TokenLifecycleManager config={config} debug={true}>
 *   <YourApp />
 * </TokenLifecycleManager>
 * ```
 */
export const TokenLifecycleManager: React.FC<TokenLifecycleManagerProps> = ({
  config,
  children,
  debug = false,
}) => {
  const [showLoading, setShowLoading] = useState(false);

  const handleRefreshStart = useCallback(() => {
    if (config.silentRenew?.showLoadingIndicator) {
      setShowLoading(true);
    }
  }, [config.silentRenew?.showLoadingIndicator]);

  const handleRefreshSuccess = useCallback(() => {
    setShowLoading(false);
  }, []);

  const handleRefreshError = useCallback((error: Error) => {
    setShowLoading(false);
    if (debug) {
      console.error('[TokenLifecycleManager] Token refresh failed:', error);
    }
    config.events?.onSilentRenewError?.(error);
  }, [config.events, debug]);

  const handleRefreshTokenExpired = useCallback(() => {
    setShowLoading(false);
    if (debug) {
      console.warn('[TokenLifecycleManager] Refresh token exceeded max lifetime');
    }
    config.events?.onTokenExpired?.();
  }, [config.events, debug]);

  const { isRefreshing, isRefreshTokenExpired } = useTokenLifecycle({
    config,
    debug,
    onRefreshStart: handleRefreshStart,
    onRefreshSuccess: handleRefreshSuccess,
    onRefreshError: handleRefreshError,
    onRefreshTokenExpired: handleRefreshTokenExpired,
  });

  // If refresh token expired, show message and require re-authentication
  if (isRefreshTokenExpired) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          backgroundColor: '#f5f5f5',
          padding: '20px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            maxWidth: '400px',
          }}
        >
          <h2 style={{ margin: '0 0 16px 0', color: '#333' }}>Session Expired</h2>
          <p style={{ margin: '0 0 24px 0', color: '#666' }}>
            Your session has been inactive for too long. Please sign in again to continue.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: '500',
            }}
          >
            Sign In Again
          </button>
        </div>
      </div>
    );
  }

  // Show loading indicator during refresh if configured
  if (showLoading && isRefreshing) {
    const message = config.silentRenew?.loadingMessage || 'Refreshing session...';

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          backgroundColor: '#f5f5f5',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e0e0e0',
              borderTop: '4px solid #007bff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>{message}</p>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Normal rendering
  return <>{children}</>;
};
